import React, { useState } from 'react';
import { DollarSign, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { maskCurrencyBRL, parseCurrencyBRL } from '../../../shared/utils/validators';
import type { User } from '../../../shared/lib/api';
import type { SalarioColaborador } from '../api';
import { useAtualizarSalario, useCriarSalario, useExcluirSalario, useSalarios } from '../hooks';

interface Props {
  /** Só id e nome: o cadastro mestre de pessoas também abre este modal. */
  colaborador: Pick<User, 'id' | 'name'>;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/** `2026-07` → `07/2026`. */
const formatCompetencia = (month: string) => {
  const [year, m] = month.split('-');
  return `${m}/${year}`;
};

const toMasked = (value: number) => maskCurrencyBRL(value.toFixed(2));

function apiError(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message ?? fallback;
}

/**
 * Histórico de salário do colaborador (CLT ou PJ). Cada registro vale da
 * competência inicial até o próximo; a liberação de NF usa o vigente.
 */
export const SalarioColaboradorModal: React.FC<Props> = ({ colaborador, onClose }) => {
  const { can } = usePermissions();
  const { data: salarios = [], isLoading } = useSalarios(colaborador.id);
  const criar = useCriarSalario(colaborador.id);
  const atualizar = useAtualizarSalario(colaborador.id);
  const excluir = useExcluirSalario(colaborador.id);

  const [editando, setEditando] = useState<SalarioColaborador | 'novo' | null>(null);
  const [amount, setAmount] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<SalarioColaborador | null>(null);

  const vigente = salarios.find((s) => s.validFrom <= new Date().toISOString().slice(0, 7));
  const salvando = criar.isPending || atualizar.isPending;

  function abrirNovo() {
    setEditando('novo');
    setAmount('');
    setValidFrom(new Date().toISOString().slice(0, 7));
    setError(null);
  }

  function abrirEdicao(s: SalarioColaborador) {
    setEditando(s);
    setAmount(toMasked(s.amount));
    setValidFrom(s.validFrom);
    setError(null);
  }

  async function salvar() {
    const valor = parseCurrencyBRL(amount);
    if (!Number.isFinite(valor) || valor <= 0) return setError('Informe um valor maior que zero');
    if (!/^\d{4}-\d{2}$/.test(validFrom)) return setError('Informe a competência de início');
    setError(null);
    try {
      if (editando === 'novo') await criar.mutateAsync({ amount: valor, validFrom });
      else if (editando) await atualizar.mutateAsync({ id: editando.id, amount: valor, validFrom });
      setEditando(null);
    } catch (err) {
      setError(apiError(err, 'Não foi possível salvar o salário.'));
    }
  }

  async function confirmarExclusao() {
    if (!removendo) return;
    try {
      await excluir.mutateAsync(removendo.id);
    } catch (err) {
      setError(apiError(err, 'Não foi possível excluir o registro.'));
    } finally {
      setRemovendo(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Salário</h2>
            <p className="mt-0.5 text-sm text-gray-500">{colaborador.name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
            <div>
              <p className="text-xs text-gray-500">Salário vigente</p>
              <p className="text-lg font-semibold text-gray-900">{vigente ? formatCurrency(vigente.amount) : '—'}</p>
            </div>
            {can(FEATURES.pessoasSalarios, 'create') && editando === null && (
              <button
                type="button"
                onClick={abrirNovo}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Plus className="h-4 w-4" /> Novo valor
              </button>
            )}
          </div>

          {editando !== null && (
            <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3">
              <p className="text-sm font-medium text-gray-800">
                {editando === 'novo' ? 'Novo salário (reajuste)' : 'Editar registro'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  label="Valor *"
                  icon={<DollarSign className="h-4 w-4" />}
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(maskCurrencyBRL(e.target.value))}
                />
                <div>
                  <label htmlFor="salario-vigencia" className="sr-only">
                    Vigente a partir de
                  </label>
                  <input
                    id="salario-vigencia"
                    type="month"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="h-14 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Vigente a partir de</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                NFs já liberadas mantêm o valor da liberação; a mudança vale para as próximas.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditando(null)}
                  disabled={salvando}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={salvar}
                  disabled={salvando}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {salvando && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
                </button>
              </div>
            </div>
          )}

          {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Histórico</p>
            {isLoading ? (
              <p className="text-sm text-gray-500">Carregando…</p>
            ) : salarios.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                Nenhum salário cadastrado. Sem salário vigente, a NF do colaborador não pode ser liberada.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {salarios.map((s) => (
                  <li key={s.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-gray-600">a partir de {formatCompetencia(s.validFrom)}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{formatCurrency(s.amount)}</span>
                      {can(FEATURES.pessoasSalarios, 'edit') && (
                        <button
                          type="button"
                          onClick={() => abrirEdicao(s)}
                          className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                          title="Editar"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {can(FEATURES.pessoasSalarios, 'delete') && (
                        <button
                          type="button"
                          onClick={() => setRemovendo(s)}
                          className="rounded p-1 text-gray-500 hover:bg-red-50 hover:text-red-700"
                          title="Excluir"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!removendo}
        title="Excluir registro de salário"
        message={
          removendo
            ? `Excluir o salário de ${formatCurrency(removendo.amount)} vigente a partir de ${formatCompetencia(removendo.validFrom)}?`
            : ''
        }
        confirmLabel="Excluir"
        danger
        busy={excluir.isPending}
        onConfirm={confirmarExclusao}
        onCancel={() => setRemovendo(null)}
      />
    </div>
  );
};
